// src/services/staffingService.js
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Logger } from '../utils/logger';

const logger = new Logger('StaffingService');

/**
 * Staffing Service - Claude-First Approach
 *
 * This service handles all staffing-related API calls following the
 * "More Intelligent - Not More Code" principle. All intelligence is
 * handled by Claude on the backend.
 */
class StaffingService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    logger.debug('StaffingService initialized with Claude-first approach');
  }

  /**
   * Find project or opportunity for staffing using natural language
   */
  async findProject(query) {
    try {
      logger.debug('Finding project for staffing:', query);

      const response = await axios.post(`${this.baseUrl}/staffing/find-project`, {
        query: query
      });

      return response.data;
    } catch (error) {
      logger.error('Error finding project for staffing:', error);
      return {
        status: 'error',
        message: error.response?.data?.error || 'Failed to find project for staffing'
      };
    }
  }

  /**
   * Get candidate suggestions for a role using Claude intelligence
   */
  async suggestCandidates(projectData, roleDetails) {
    try {
      logger.debug('Getting candidate suggestions for role:', roleDetails.role_name);

      const response = await axios.post(`${this.baseUrl}/staffing/suggest-candidates`, {
        project_data: projectData,
        role_details: roleDetails
      });

      return response.data;
    } catch (error) {
      logger.error('Error getting candidate suggestions:', error);
      return {
        status: 'error',
        message: error.response?.data?.error || 'Failed to get candidate suggestions',
        candidates: []
      };
    }
  }

  /**
   * Get detailed explanation of candidate match using Claude
   */
  async explainMatch(projectData, roleDetails, employee) {
    try {
      logger.debug('Getting match explanation for employee:', employee.employee_id);

      const response = await axios.post(`${this.baseUrl}/staffing/explain-match`, {
        project_data: projectData,
        role_details: roleDetails,
        employee: employee
      });

      return response.data;
    } catch (error) {
      logger.error('Error getting match explanation:', error);
      return {
        status: 'error',
        message: error.response?.data?.error || 'Failed to get match explanation'
      };
    }
  }

  /**
   * Analyze team composition using Claude intelligence
   */
  async analyzeTeam(projectData, assignedEmployees) {
    try {
      logger.debug('Analyzing team composition for project:', projectData.id);

      const response = await axios.post(`${this.baseUrl}/staffing/team-analysis`, {
        project_data: projectData,
        assigned_employees: assignedEmployees
      });

      return response.data;
    } catch (error) {
      logger.error('Error analyzing team composition:', error);
      return {
        status: 'error',
        message: error.response?.data?.error || 'Failed to analyze team composition'
      };
    }
  }

  /**
   * Initialize staffing workflow
   */
  async initializeWorkflow(projectId, projectType, roles) {
    try {
      logger.debug('Initializing staffing workflow for project:', projectId);

      const response = await axios.post(`${this.baseUrl}/staffing/initialize`, {
        project_id: projectId,
        project_type: projectType,
        roles: roles
      });

      return response.data;
    } catch (error) {
      logger.error('Error initializing staffing workflow:', error);
      throw error;
    }
  }

  /**
   * Get existing workflow state
   */
  async getWorkflowState(projectId) {
    try {
      const response = await axios.get(`${this.baseUrl}/staffing/workflow/${projectId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No existing workflow
      }
      logger.error('Error getting workflow state:', error);
      throw error;
    }
  }

  /**
   * Save staffing progress
   */
  async saveProgress(projectId, assignments) {
    try {
      logger.debug('Saving staffing progress for project:', projectId);

      const response = await axios.post(`${this.baseUrl}/staffing/save-progress`, {
        project_id: projectId,
        assignments: assignments
      });

      return response.data;
    } catch (error) {
      logger.error('Error saving staffing progress:', error);
      throw error;
    }
  }

  /**
   * Complete staffing workflow
   */
  async completeStaffing(projectId) {
    try {
      logger.debug('Completing staffing for project:', projectId);

      const response = await axios.post(`${this.baseUrl}/staffing/complete`, {
        project_id: projectId
      });

      return response.data;
    } catch (error) {
      logger.error('Error completing staffing:', error);
      throw error;
    }
  }

  /**
   * Get staffing workflow summary
   */
  async getWorkflowSummary(projectId) {
    try {
      const response = await axios.get(`${this.baseUrl}/staffing/summary/${projectId}`);
      return response.data;
    } catch (error) {
      logger.error('Error getting workflow summary:', error);
      return {
        status: 'error',
        message: error.response?.data?.error || 'Failed to get workflow summary'
      };
    }
  }

  /**
   * Check if multiple staffing workflows are active (for preventing conflicts)
   */
  async getActiveWorkflows() {
    try {
      const response = await axios.get(`${this.baseUrl}/staffing/active-workflows`);
      return response.data;
    } catch (error) {
      logger.error('Error getting active workflows:', error);
      return [];
    }
  }
}

const staffingService = new StaffingService();
export default staffingService;
